import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Box, MenuItem, Select, Typography } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';

import { ReportType } from '../../report-requests.types';
import type { ReportTypeSelectorProps } from './ReportTypeSelector.types';

const REPORT_TYPE_ITEMS: Array<{
  value: ReportType;
  titleKey: string;
  descriptionKey: string;
}> = [
  {
    value: ReportType.EmployeeInformation,
    titleKey: 'reports.type.employeeInfo.title',
    descriptionKey: 'reports.type.employeeInfo.description',
  },
  {
    value: ReportType.ActiveEmployeeCount,
    titleKey: 'reports.type.activeEmployeeCount.title',
    descriptionKey: 'reports.type.activeEmployeeCount.description',
  },
  {
    value: ReportType.SummaryOfRegulationDeviations,
    titleKey: 'reports.type.summaryOfRegulationDeviations.title',
    descriptionKey: 'reports.type.summaryOfRegulationDeviations.description',
  },
  {
    value: ReportType.InterruptedSynchronization,
    titleKey: 'reports.type.interruptedSynchronization.title',
    descriptionKey: 'reports.type.interruptedSynchronization.description',
  },
  {
    value: ReportType.LatestConnectionRun,
    titleKey: 'reports.type.latestConnectionRun.title',
    descriptionKey: 'reports.type.latestConnectionRun.description',
  },
  {
    value: ReportType.CollectiveSalaryChange,
    titleKey: 'reports.type.collectiveSalaryChange.title',
    descriptionKey: 'reports.type.collectiveSalaryChange.description',
  },
  {
    value: ReportType.ControlTool,
    titleKey: 'reports.type.controlTool.title',
    descriptionKey: 'reports.type.controlTool.description',
  },
  {
    value: ReportType.SicknessLeave,
    titleKey: 'reports.type.sicknessLeave.title',
    descriptionKey: 'reports.type.sicknessLeave.description',
  },
];

export const ReportTypeSelector = ({ value, onChange }: ReportTypeSelectorProps) => {
  const intl = useIntl();

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value as ReportType);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography
        sx={{
          lineHeight: '34px',
          textTransform: 'none',
          fontSize: '0.8125rem',
          color: 'primary.dark',
          whiteSpace: 'nowrap',
        }}
      >
        {intl.formatMessage({ id: 'reporting.selectReportType' })}
      </Typography>
      <Select
        value={value}
        onChange={handleChange}
        sx={{
          minWidth: 280,
          '& .MuiSelect-select': {
            fontWeight: 650,
            color: 'primary.dark',
          },
        }}
        renderValue={selected => {
          const item = REPORT_TYPE_ITEMS.find(i => i.value === selected);
          return item ? intl.formatMessage({ id: item.titleKey }) : '';
        }}
      >
        {REPORT_TYPE_ITEMS.map(item => (
          <MenuItem key={item.value} value={item.value}>
            <Box sx={{ maxWidth: 400, overflowWrap: 'break-word', whiteSpace: 'normal' }}>
              <Typography sx={{ fontWeight: 650, lineHeight: '1.2em', py: '5px' }}>
                <FormattedMessage id={item.titleKey} />
              </Typography>
              <Typography sx={{ color: 'primary.dark' }}>
                <FormattedMessage id={item.descriptionKey} />
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};
